import { useMemo } from 'react'
import { describeLink, track } from '@/lib/analytics'
import { decorateContactHref, getTraffic } from '@/lib/analytics/traffic'
import type { ContactContent } from '@/lib/content.types'
import { fluidHeadingSize } from '@/lib/typography'

export function Contact({ data }: { data: ContactContent }) {
  /**
   * 문의 폼(예: https://contact.annx.ai)으로 갈 때 방문자의 유입 정보(utm_*, ref)를
   * 링크 뒤에 붙여 넘긴다 → 폼이 문의 메일에 함께 기록한다.
   * 주소는 Admin 의 `contact.cta.href` 에서 오므로 주소만 바꿔도 그대로 동작한다.
   * mailto:·#앵커는 건드리지 않는다 (`lib/analytics/traffic.ts` 참고).
   */
  const href = useMemo(() => decorateContactHref(data.cta.href), [data.cta.href])

  const onClick = () => {
    // link_url 은 Admin 에 등록된 원래 주소 — 방문자마다 붙는 쿼리 때문에 값이 갈라지지 않게
    const link = describeLink(data.cta.href)
    const passed = href !== data.cta.href
    const last = getTraffic().last
    track('Contact CTA Clicked', {
      cta_label: data.cta.label,
      ...link,
      // 폼으로 넘긴 유입 정보 — 실제로 링크에 붙였을 때만
      passed_attribution: passed,
      passed_utm_source: passed ? last.source : undefined,
      passed_utm_medium: passed ? last.medium : undefined,
      passed_ref: passed ? last.ref : undefined,
    })
  }

  return (
    <section
      id="contact"
      className="relative overflow-hidden px-5 py-24 md:px-10 md:py-[112px]"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[560px] w-[820px] max-w-[130vw] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[26px]"
        style={{
          background:
            'radial-gradient(52% 54% at 44% 44%,rgba(46,144,250,.36),transparent 62%),radial-gradient(46% 48% at 62% 60%,rgba(95,18,204,.30),transparent 64%)',
        }}
      />
      <div className="relative mx-auto max-w-[720px] text-center">
        <h2
          // 줄간격은 Hero 큰 제목과 같은 값(leading-headline)을 쓴다
          className="mb-[18px] font-headline font-bold leading-headline tracking-headline"
          // Admin에서 정한 크기를 큰 화면 상한으로 쓰고, 좁은 화면에서는 자동 축소
          style={{ fontSize: fluidHeadingSize(data.headingSize) }}
        >
          {data.heading}
        </h2>
        <p className="mb-[34px] text-[17px] leading-[1.6] text-ink-secondary md:text-lg">
          {data.body}
        </p>
        {/* 높이는 .annx-cta 가 관리한다 — 상단 Hero 버튼과 정확히 같은 높이 */}
        <a
          href={href}
          onClick={onClick}
          // 가운데 클릭으로 새 탭에 여는 경우도 문의 클릭으로 센다
          onAuxClick={(e) => {
            if (e.button === 1) onClick()
          }}
          className="annx-cta rounded-[11px] px-[34px] text-base text-white shadow-cta-contact transition-[filter] hover:brightness-110"
          style={{ background: 'var(--accent)' }}
        >
          {data.cta.label}
        </a>
      </div>
    </section>
  )
}
